import type { ReactNode } from "react";
import { NavLink } from "../../router";
import type { Permission } from "../../api/types";
import { useSession } from "../../state/session";
import { surfaceHref, SURFACE_TITLES, type Surface } from "../../surface";
import { TopBar } from "../../ui/TopBar";

interface NavItem {
  to: string;
  title: string;
  permission?: Permission;
  end?: boolean;
}

const SECTIONS: { group: string; items: NavItem[] }[] = [
  {
    group: "Работа",
    items: [
      { to: "/", title: "Главная", end: true },
      { to: "/orders", title: "Заказы", permission: "orders.view" },
      { to: "/payments", title: "Платежи и возвраты", permission: "orders.view" },
      { to: "/customers", title: "Клиенты", permission: "customers.view" },
      { to: "/devices", title: "Устройства", permission: "devices.manage" },
      { to: "/fiscal", title: "Чеки и маркировка", permission: "devices.manage" },
      { to: "/stock", title: "Склад", permission: "stock.view" },
      { to: "/purchases", title: "Закупки", permission: "stock.view" },
      { to: "/analytics", title: "Аналитика", permission: "analytics.view" }
    ]
  },
  {
    group: "Настройка",
    items: [
      { to: "/menu", title: "Меню и стоп-лист", permission: "stoplist.manage" },
      { to: "/promotions", title: "Акции", permission: "promotions.manage" },
      { to: "/promo-codes", title: "Промокоды", permission: "promotions.manage" },
      { to: "/banners", title: "Баннеры витрины", permission: "promotions.manage" },
      { to: "/benches", title: "Лавочки и QR", permission: "benches.manage" },
      { to: "/settings", title: "Настройки точки", permission: "settings.manage" },
      { to: "/storefront", title: "Оформление витрины", permission: "settings.manage" },
      { to: "/staff", title: "Сотрудники", permission: "staff.manage" },
      { to: "/roles", title: "Роли и права", permission: "staff.manage" },
      { to: "/audit", title: "Журнал действий", permission: "audit.view" }
    ]
  }
];

/**
 * Соседние поверхности: они живут на других поддоменах, поэтому это обычные
 * ссылки, а не переходы роутера.
 */
const NEIGHBOURS: { surface: Exclude<Surface, "unknown">; permission: Permission }[] = [
  { surface: "counter", permission: "orders.counter" },
  { surface: "kitchen", permission: "orders.status.kitchen" },
  { surface: "courier", permission: "orders.claim" }
];

export function AdminLayout({ children }: { children: ReactNode }) {
  const { can } = useSession();

  const neighbours = NEIGHBOURS.filter(item => can(item.permission));

  return (
    <div className="shell">
      <TopBar title="Админка" subtitle="управление точкой" />

      <div className="admin">
        <nav className="side" aria-label="Разделы админки">
          {SECTIONS.map(section => {
            const items = section.items.filter(item => !item.permission || can(item.permission));
            if (items.length === 0) return null;

            return (
              <div key={section.group}>
                <p className="side-group">{section.group}</p>
                {items.map(item => (
                  <NavLink key={item.to} to={item.to} end={item.end}>
                    {item.title}
                  </NavLink>
                ))}
              </div>
            );
          })}

          {neighbours.length > 0 ? (
            <div>
              <p className="side-group">Другие экраны</p>
              {neighbours.map(item => (
                <a key={item.surface} href={surfaceHref(item.surface)}>
                  {SURFACE_TITLES[item.surface]}
                </a>
              ))}
            </div>
          ) : null}
        </nav>

        <main className="page">{children}</main>
      </div>
    </div>
  );
}
